import RestClient from './RestClient';
import Change from '../models/Change';
import { getStore } from './getStore';
import type { FetchOptions } from './Client';
import {
    normalizeUiLanguage,
    pickUiTranslation,
    UI_LANGUAGE_NAMES,
} from '../utils/detectMessageLanguage';

class Changes extends RestClient {
    constructor() {
        super({
            model: Change,
            entryPoint: 'changes',
            sortBy: 'title.fr',
            id_field: '_id',
            // redux config
            resource: 'change',
            resources: 'changes',
        });
    }

    toStep(_id: string, step: string, params?: AnyObj) {
        params = { _id, step };

        return this.PUT(`/${this.entryPoint}/${_id}/step`, params)
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    move(_id: string, projectId: string, boardId?: string, columnId?: string) {
        const params = { _id, projectId, boardId, columnId };

        return this.PUT(`/${this.entryPoint}/${_id}/move`, params)
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    addQuote(_id: string, params?: AnyObj) {
        params = { _id };
        return this.POST(`/${this.entryPoint}/${_id}/quote`, params)
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    // Create a Change
    createBugReport(_id: string, bugReportData: AnyObj) {
        let data = {...bugReportData, _id};
        return this.POST(`/support`, data)
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json.change;
                }
                throw new Error(json.flash.error);
            });
    }

    // Adds a Change to the support board
    addChangeToColumn(projectId: string, columnId: string, changeId: string) {
        return this.PUT(`/support/${projectId}/project`, { columnId, changeId })
            .then(response => response.json())
            .then(json => {
                if (!json.success){
                    throw new Error(json);
                }
                return json;
            });
    }

    setImageRelease(id: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);

        const headers: AnyObj = {};
        const subscriberId = getStore().getState()?.mercure?.subscriberId;
        if (subscriberId)
            headers['X-MERCURE-SUBSCRIBER'] = subscriberId;

        let url = `${this.baseUrl}/${this.entryPoint}/${id}/releaseImage`;
        const options: FetchOptions = {
            method: 'POST',
            body: formData,
            headers,
            credentials: 'include',
        };

        return fetch(url, options)
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    unsetImageRelease(id: string) {
        return this.DELETE(`/${this.entryPoint}/${id}/releaseImage`)
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    suggestReply(payload: AnyObj) {
        return this.POST('/ai/suggest-reply', payload)
            .then(response => response.json())
            .then(json => {
                if (json.suggestion?.reply)
                    return json.suggestion;

                throw new Error(json.err || json.error || 'Could not suggest reply');
            });
    }

    /**
     * Translate HTML/text via server OpenAI helper into the UI language.
     * Expects `{ translations: { [langCode]: html } }` and returns only that locale.
     */
    translate(payload: { text: string; to: string; context?: string }) {
        const lang = normalizeUiLanguage(payload.to);
        const languageName = lang ? UI_LANGUAGE_NAMES[lang] : String(payload.to || '');
        const request = {
            ...payload,
            // OpenAI prompt embeds `to` verbatim — use a clear language name.
            to: languageName,
            context: [
                payload.context || '',
                lang
                    ? `Translate only into ${languageName}. Output JSON as {"${lang}":"<translated html>"}. Do not include other languages.`
                    : '',
            ].filter(Boolean).join(' '),
        };

        return this.POST('/ai/translate', request)
            .then(response => response.json())
            .then(json => {
                const translations = json.translations || {};
                const translated = lang
                    ? pickUiTranslation(translations, lang)
                    : null;

                if (typeof translated === 'string' && translated.trim())
                    return translated;

                throw new Error(json.err || json.error || 'Could not translate message');
            });
    }

    suggestLabels(payload: AnyObj) {
        return this.POST('/ai/suggest-labels', payload)
            .then(response => response.json())
            .then(json => {
                if (json.suggestion?.labels)
                    return json;

                throw new Error(json.err || json.error || 'Could not suggest labels');
            });
    }

    sendReply(_id: string, payload: AnyObj) {
        return this.POST(`/${this.entryPoint}/${_id}/reply`, payload)
            .then(response => response.json())
            .then(json => {
                if (json.comment)
                    return json;

                throw new Error(json.err || json.error || 'Could not send reply');
            });
    }

    sendForward(_id: string, payload: AnyObj) {
        return this.POST(`/${this.entryPoint}/${_id}/forward`, payload)
            .then(response => response.json())
            .then(json => {
                if (json.comment)
                    return json;

                throw new Error(json.err || json.error || 'Could not forward message');
            });
    }

    previewReplyRecipients(_id: string, payload: AnyObj) {
        return this.POST(`/${this.entryPoint}/${_id}/reply-preview`, payload)
            .then(response => response.json())
            .then(json => {
                if (json.preview)
                    return json;

                throw new Error(json.err || json.error || 'Could not preview reply recipients');
            });
    }

    previewForwardRecipients(_id: string, payload: AnyObj) {
        return this.POST(`/${this.entryPoint}/${_id}/forward-preview`, payload)
            .then(response => response.json())
            .then(json => {
                if (json.preview)
                    return json;

                throw new Error(json.err || json.error || 'Could not preview forward recipients');
            });
    }

    updateReplyRecipients(_id: string, replyRecipients: AnyObj) {
        return this.PUT(`/${this.entryPoint}/${_id}/reply-recipients`, { replyRecipients })
            .then(response => response.json())
            .then(json => {
                if (json.change)
                    return json;

                throw new Error(json.err || json.error || 'Could not update reply recipients');
            });
    }

    subscribeNotifications(_id: string) {
        return this.POST(`/${this.entryPoint}/${_id}/subscriptions`, {})
            .then(response => response.json())
            .then(json => {
                if (json.change)
                    return new Change(json.change);
                throw new Error(json.err || json.error || 'Could not subscribe to change notifications');
            });
    }

    unsubscribeNotifications(_id: string) {
        return this.DELETE(`/${this.entryPoint}/${_id}/subscriptions`)
            .then(response => response.json())
            .then(json => {
                if (json.change)
                    return new Change(json.change);
                throw new Error(json.err || json.error || 'Could not unsubscribe from change notifications');
            });
    }

    updateSubscribers(_id: string, subscribers: string[]) {
        return this.PUT(`/${this.entryPoint}/${_id}/subscribers`, { subscribers })
            .then(response => response.json())
            .then(json => {
                if (json.change)
                    return new Change(json.change);
                throw new Error(json.err || json.error || 'Could not update change subscribers');
            });
    }

    addRelated(_id: string, relatedId: string) {
        return this.POST(`/${this.entryPoint}/${_id}/related`, { relatedId })
            .then(response => response.json())
            .then(json => {
                if (json.change)
                    return new Change(json.change);
                throw new Error(json.err || json.error || 'Could not link related change');
            });
    }

    setFollowUp(_id: string, targetId: string) {
        return this.PUT(`/${this.entryPoint}/${_id}/follow-up`, { targetId })
            .then(response => response.json())
            .then(json => {
                if (json.change)
                    return new Change(json.change);
                throw new Error(json.err || json.error || 'Could not set follow-up change');
            });
    }

    removeRelated(_id: string, relatedId: string) {
        return this.DELETE(`/${this.entryPoint}/${_id}/related/${relatedId}`)
            .then(response => response.json())
            .then(json => {
                if (json.change)
                    return new Change(json.change);
                throw new Error(json.err || json.error || 'Could not unlink related change');
            });
    }

    claimFromTriage(_id: string, assignee?: string) {
        return this.POST(`/${this.entryPoint}/${_id}/claim-from-triage`, assignee ? { assignee } : {})
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }

                throw new Error(json.err || json.error || 'Could not claim card from triage');
            });
    }

    triageModify(_id: string, payload: { assignee?: string; projectId?: string }) {
        return this.POST(`/${this.entryPoint}/${_id}/triage-modify`, payload)
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.err || json.error || 'Could not modify triage card');
            });
    }

    resolveFromTriage(_id: string, payload?: { assignee?: string; projectId?: string }) {
        return this.POST(`/${this.entryPoint}/${_id}/resolve-from-triage`, payload || {})
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.err || json.error || 'Could not resolve triage card');
            });
    }

    discardFromTriage(_id: string) {
        return this.POST(`/${this.entryPoint}/${_id}/discard-from-triage`, {})
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.err || json.error || 'Could not discard triage card');
            });
    }

    restoreFromTriage(_id: string) {
        return this.POST(`/${this.entryPoint}/${_id}/restore-from-triage`, {})
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.err || json.error || 'Could not restore triage card');
            });
    }

    /**
     * Allowlist the card sender email or domain and move spam_suspect → pendingTriage.
     */
    trustSpamSender(_id: string, scope: 'email' | 'domain') {
        return this.POST(`/${this.entryPoint}/${_id}/trust-spam-sender`, { scope })
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }
                throw new Error(json.err || json.error || 'Could not trust spam sender');
            });
    }

    /**
     * Apply one triage action to many cards in a single request.
     * Partial success returns `{ changes, errors }` (HTTP 200).
     */
    bulkFromTriage(payload: {
        action: 'resolve' | 'discard' | 'restore' | 'modify';
        changeIds: string[];
        assignee?: string;
        projectId?: string;
    }) {
        return this.POST(`/${this.entryPoint}/bulk-from-triage`, payload)
            .then(response => response.json())
            .then(json => {
                const changes = (json.changes || []).map((item: AnyObj) => new Change(item));
                const errors = Array.isArray(json.errors) ? json.errors : [];
                if (changes.length)
                    return { changes, errors };
                throw new Error(json.err || json.error || 'Could not apply bulk triage action');
            });
    }

    merge(targetId: string, sourceIds: string[]) {
        return this.POST(`/${this.entryPoint}/merge`, { targetId, sourceIds })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (response.ok && json.change) {
                    json.change = new Change(json.change);
                    json.sources = (json.sources || []).map((item: AnyObj) => new Change(item));
                    return json;
                }
                throw new Error(json.err || json.error || 'Could not merge changes');
            });
    }

    getBySlug(slug: string | number, projectId?: string) {
        const query: AnyObj = {};
        if (projectId)
            query.project = projectId;

        return this.GET(`/${this.entryPoint}/by-slug/${slug}`, query)
            .then(response => response.json())
            .then(json => {
                if (json.change) {
                    json.change = new Change(json.change);
                    return json;
                }

                throw new Error(json.err || json.error || 'Change not found');
            });
    }

    getMergeRequest(changeId: string) {
        return this.GET(`/${this.entryPoint}/${changeId}/merge-request`)
            .then(async (response) => {
                const json = await response.json();
                if (!response.ok)
                    throw new Error(json.err || json.error || 'Failed to load merge request');
                return json;
            });
    }

    private postMergeRequestAction(changeId: string, action: string, body: AnyObj = {}, fallbackError: string) {
        return this.POST(`/${this.entryPoint}/${changeId}/merge-request/${action}`, body)
            .then(async (response) => {
                const json = await response.json();
                if (!response.ok) {
                    const err = new Error(json.err || json.error || fallbackError) as Error & { code?: string };
                    err.code = json.code;
                    throw err;
                }
                return json;
            });
    }

    approveMergeRequest(changeId: string) {
        return this.postMergeRequestAction(changeId, 'approve', {}, 'Failed to approve merge request');
    }

    mergeMergeRequest(changeId: string, body: { should_remove_source_branch?: boolean } = {}) {
        return this.postMergeRequestAction(changeId, 'merge', body, 'Failed to merge');
    }

    commentMergeRequest(changeId: string, body: string) {
        return this.postMergeRequestAction(
            changeId,
            'comment',
            { body },
            'Failed to comment on merge request'
        );
    }

    rebaseMergeRequest(changeId: string, body: { skip_ci?: boolean } = {}) {
        return this.postMergeRequestAction(changeId, 'rebase', body, 'Failed to rebase merge request');
    }

    approveAndMerge(changeId: string, body: { should_remove_source_branch?: boolean } = {}) {
        return this.postMergeRequestAction(
            changeId,
            'approve-and-merge',
            body,
            'Failed to approve and merge'
        );
    }
}

const ChangesApi = new Changes();
export default ChangesApi;
